import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { IssueListItem, IssuePagination } from '../../services/issueService';
import { resolveIssueTypeConfig } from '../../constants/issueTypeConfig';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';

interface IssueListViewProps {
  items: IssueListItem[];
  pagination: IssuePagination;
  page: number;
  pageSize: number;
  sort: string;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSortChange: (sort: string) => void;
  onIssueClick: (issue: IssueListItem) => void;
  showProjectColumn?: boolean;
  /** 정렬 셀렉터 좌측에 들어갈 추가 컨트롤 (예: 사이드 패널 보기 토글) */
  headerExtra?: ReactNode;
}

const PRIORITY_BADGE: Record<string, { label: string; class: string }> = {
  BLOCKER: { label: 'BLOCKER', class: 'bg-black text-white' },
  CRITICAL: { label: 'CRITICAL', class: 'bg-red-600 text-white' },
  HIGH: { label: 'HIGH', class: 'bg-red-100 text-red-700' },
  MEDIUM: { label: 'MEDIUM', class: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'LOW', class: 'bg-blue-100 text-blue-700' },
};

const SORT_OPTIONS = [
  { value: 'created:desc', label: '생성일 최신순' },
  { value: 'created:asc', label: '생성일 오래된순' },
  { value: 'updated:desc', label: '수정일 최신순' },
  { value: 'priority:asc', label: '우선순위 높은순' },
  { value: 'priority:desc', label: '우선순위 낮은순' },
  { value: 'dueDate:asc', label: '마감일 가까운순' },
];

/** CHG-041: 워크플로우 색상 기반 대비 텍스트 색상 */
function getContrastText(hexColor: string): string {
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#374151' : '#FFFFFF';
}

export default function IssueListView({
  items,
  pagination,
  page,
  pageSize,
  sort,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onIssueClick,
  showProjectColumn,
  headerExtra,
}: IssueListViewProps) {
  const { openProfileModal } = useProfileModal();
  const colCount = showProjectColumn ? 9 : 8;
  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Header bar */}
      <div className="flex items-center justify-between mb-3 shrink-0">
        <span className="text-sm text-gray-600">
          총 <span className="font-semibold text-gray-900">{pagination.total}</span>건
        </span>
        <div className="flex items-center gap-3">
          {headerExtra}
          <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">정렬:</span>
          <select
            value={sort}
            onChange={(e) => onSortChange(e.target.value)}
            className="text-sm border border-gray-300 rounded-lg px-2 py-1 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 border border-gray-200 rounded-lg overflow-auto bg-white">
        <table className="w-full text-sm min-w-[960px]">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-[100px] whitespace-nowrap">키</th>
              {showProjectColumn && <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-[80px] whitespace-nowrap">프로젝트</th>}
              <th className="text-center px-4 py-2.5 font-medium text-gray-600 w-[50px] whitespace-nowrap">유형</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-600 min-w-[240px]">제목</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-[100px] whitespace-nowrap">담당자</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-[90px] whitespace-nowrap">우선순위</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-[90px] whitespace-nowrap">상태</th>
              <th className="text-left px-4 py-2.5 font-medium text-gray-600 w-[100px] whitespace-nowrap">상위 작업</th>
              <th className="text-center px-4 py-2.5 font-medium text-gray-600 w-[50px] whitespace-nowrap">하위</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400">
                  작업이 없습니다.
                </td>
              </tr>
            )}
            {items.map((issue) => {
              const priority = PRIORITY_BADGE[issue.priority];
              const ws = issue.workflowStatus;
              return (
                <tr
                  key={issue.id}
                  onClick={() => onIssueClick(issue)}
                  className="border-b border-gray-100 last:border-0 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-2.5 font-mono text-sm text-gray-500 whitespace-nowrap">
                    {issue.issueId}
                  </td>
                  {showProjectColumn && (
                    <td className="px-4 py-2.5">
                      <span className="px-1.5 py-0.5 bg-gray-100 text-gray-700 text-[11px] font-bold rounded">
                        {(issue as unknown as { project?: { projectKey?: string } }).project?.projectKey}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-2.5 text-center">
                    {(() => {
                      const cfg = resolveIssueTypeConfig(issue.issueTypeDef, issue.issueType);
                      return <cfg.Icon size={16} className={cfg.color} style={cfg.hexColor ? { color: cfg.hexColor } : undefined} />;
                    })()}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium text-gray-900 truncate max-w-md">
                      {issue.title}
                    </div>
                    {issue.labels.length > 0 && (
                      <div className="flex gap-1 mt-0.5">
                        {issue.labels.slice(0, 3).map((l) => (
                          <span key={l} className="px-1.5 py-0.5 text-[11px] bg-gray-100 text-gray-500 rounded">
                            {l}
                          </span>
                        ))}
                        {issue.labels.length > 3 && (
                          <span className="text-[11px] text-gray-400">+{issue.labels.length - 3}</span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {issue.assignee ? (
                      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                        <Avatar
                          size="xs"
                          userId={issue.assignee.id}
                          name={issue.assignee.name}
                          avatarUrl={issue.assignee.avatarUrl}
                          displayColor={issue.assignee.displayColor as ProfileColorKey | null | undefined}
                          firstName={issue.assignee.firstName}
                          onClick={() => openProfileModal(issue.assignee!.id)}
                        />
                        <span className="text-sm text-gray-700">{issue.assignee.name}</span>
                      </div>
                    ) : (
                      <span className="text-sm text-gray-400">미할당</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 text-[11px] font-medium rounded ${priority?.class ?? ''}`}>
                      {priority?.label ?? issue.priority}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <span
                      className="inline-block px-2 py-0.5 text-[11px] font-medium rounded"
                      style={{
                        backgroundColor: ws.color + '22',
                        color: getContrastText(ws.color) === '#FFFFFF' ? ws.color : getContrastText(ws.color),
                      }}
                    >
                      {ws.name}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    {issue.parentIssue ? (
                      <span className="font-mono text-sm text-blue-600 hover:underline">
                        {issue.parentIssue.issueId}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-300">-</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className="text-sm text-gray-500">
                      {issue._count?.childIssues || 0}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3 shrink-0">
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => onPageChange(page - 1)}
            className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={16} />
          </button>
          {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, pagination.totalPages - 4));
            const pageNum = start + i;
            if (pageNum > pagination.totalPages) return null;
            return (
              <button
                key={pageNum}
                onClick={() => onPageChange(pageNum)}
                className={`w-8 h-8 text-sm rounded-lg ${
                  pageNum === page
                    ? 'bg-primary-600 text-white font-medium'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {pageNum}
              </button>
            );
          })}
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => onPageChange(page + 1)}
            className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="text-sm border border-gray-300 rounded-lg px-2 py-1"
        >
          <option value={25}>25건/페이지</option>
          <option value={50}>50건/페이지</option>
          <option value={100}>100건/페이지</option>
        </select>
      </div>
    </div>
  );
}

export { PRIORITY_BADGE, getContrastText };
